import { useMutation, useQuery } from "@tanstack/react-query";
import type { SetupChecklistItem, SetupChecklistState } from "@unbox/api";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, type MerchantScope, useScopedMerchantClient } from "./scoped-merchant-client";

const queryKey = (scope: MerchantScope) => ["setup-checklist", scope.merchantId, scope.workspaceId];

export const useSetupChecklist = (enabled: boolean) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: queryKey(scope),
    queryFn: async (): Promise<SetupChecklistState> => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["setup-checklist"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["setup-checklist"].$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!scope.merchantId,
    staleTime: 60_000,
  });
};

export const useCompleteSetupItem = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (item: SetupChecklistItem) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["setup-checklist"].items[
            ":item"
          ].complete.$post({ param: { ...scope.param, item } })
        : await client.merchant[":merchantId"]["setup-checklist"].items[":item"].complete.$post({
            param: { ...scope.param, item },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(scope) }),
  });
};

export const useIgnoreSetupItem = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ item, ignored }: { item: SetupChecklistItem; ignored: boolean }) => {
      const res = scope.workspaceId
        ? ignored
          ? await client.workspace[":workspaceId"].merchant[":merchantId"]["setup-checklist"].items[
              ":item"
            ].ignore.$post({ param: { ...scope.param, item } })
          : await client.workspace[":workspaceId"].merchant[":merchantId"]["setup-checklist"].items[
              ":item"
            ].ignore.$delete({ param: { ...scope.param, item } })
        : ignored
          ? await client.merchant[":merchantId"]["setup-checklist"].items[":item"].ignore.$post({
              param: { ...scope.param, item },
            })
          : await client.merchant[":merchantId"]["setup-checklist"].items[":item"].ignore.$delete({
              param: { ...scope.param, item },
            });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(scope) }),
  });
};

export const useMarkSetupChecklistSeen = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["setup-checklist"].seen.$post({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["setup-checklist"].seen.$post({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(scope) }),
  });
};

export const useDismissSetupChecklist = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["setup-checklist"].dismiss.$post({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["setup-checklist"].dismiss.$post({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(scope) }),
  });
};
